import {
    cloneDisplayConfiguration,
    createDisplayConfiguration,
    reconcileDisplayEntries,
} from '../helpers/assetSetVisualizationModel';

export type AssetSetVisualizationChangeSource = 'normal' | 'ontology-switch-clear';

export interface AssetSetVisualizationChange {
    configuration: IAssetSetDisplayConfiguration | null;
    version: number;
    source: AssetSetVisualizationChangeSource;
}

class AssetSetVisualizationStore {
    private configuration: IAssetSetDisplayConfiguration | null = null;
    private version = 0;
    private readonly changeListeners = new Set<(change: AssetSetVisualizationChange) => void>();
    private readonly assetSetsChangeListeners = new Set<(facilityID: string, assetSetID?: string) => void>();
    private readonly populationStatusByAssetSetID = new Map<string, 'ready' | 'pending' | 'error'>();
    private readonly populationRetryListeners = new Set<(assetSetID: string) => void>();

    private notifyChange(source: AssetSetVisualizationChangeSource = 'normal') {
        const change: AssetSetVisualizationChange = {
            configuration: this.configuration,
            version: this.version,
            source,
        };
        [...this.changeListeners].forEach(callback => callback(change));
    }

    private commit(
        next: IAssetSetDisplayConfiguration | null,
        source: AssetSetVisualizationChangeSource = 'normal'
    ) {
        this.configuration = next;
        this.version++;
        this.notifyChange(source);
    }

    getConfiguration() { return this.configuration; }
    getVersion() { return this.version; }
    addChangeListener(callback: (change: AssetSetVisualizationChange) => void) { this.changeListeners.add(callback); }
    removeChangeListener(callback: (change: AssetSetVisualizationChange) => void) { this.changeListeners.delete(callback); }
    addAssetSetsChangeListener(callback: (facilityID: string, assetSetID?: string) => void) { this.assetSetsChangeListeners.add(callback); }
    removeAssetSetsChangeListener(callback: (facilityID: string, assetSetID?: string) => void) { this.assetSetsChangeListeners.delete(callback); }
    notifyAssetSetsChanged(facilityID: string, assetSetID?: string) {
        [...this.assetSetsChangeListeners].forEach(callback => callback(facilityID, assetSetID));
    }

    initialize(facilityID: string, allFacilitySets: readonly IAssetSet[], all3DAssetsID: string, allAssetTagsID: string) {
        if (!this.configuration || this.configuration.FacilityID !== facilityID) {
            this.populationStatusByAssetSetID.clear();
            this.commit(createDisplayConfiguration(facilityID, all3DAssetsID, allAssetTagsID));
            return this.configuration;
        }
        return this.reconcile(facilityID, allFacilitySets, all3DAssetsID, allAssetTagsID);
    }

    reconcile(facilityID: string, allFacilitySets: readonly IAssetSet[], all3DAssetsID: string, allAssetTagsID: string) {
        if (!this.configuration || this.configuration.FacilityID !== facilityID) return this.configuration;
        const next = reconcileDisplayEntries(this.configuration, allFacilitySets, all3DAssetsID, allAssetTagsID);
        if (next !== this.configuration) this.commit(next);
        return this.configuration;
    }

    update(
        nextConfiguration: IAssetSetDisplayConfiguration,
        source: AssetSetVisualizationChangeSource = 'normal'
    ) {
        if (this.configuration?.FacilityID !== nextConfiguration.FacilityID) return;
        this.commit(cloneDisplayConfiguration(nextConfiguration), source);
    }

    getPopulationStatuses() { return new Map(this.populationStatusByAssetSetID); }
    setPopulationStatus(assetSetIDs: readonly string[], status: 'ready' | 'pending' | 'error') {
        let changed = false;
        assetSetIDs.forEach(assetSetID => {
            const key = assetSetID.toLocaleLowerCase();
            if (this.populationStatusByAssetSetID.get(key) === status) return;
            this.populationStatusByAssetSetID.set(key, status);
            changed = true;
        });
        if (changed) this.notifyChange();
    }
    retainPopulationStatuses(assetSetIDs: readonly string[]) {
        const retained = new Set(assetSetIDs.map(id => id.toLocaleLowerCase()));
        let changed = false;
        [...this.populationStatusByAssetSetID.keys()].forEach(key => {
            if (retained.has(key)) return;
            this.populationStatusByAssetSetID.delete(key);
            changed = true;
        });
        if (changed) this.notifyChange();
    }
    addPopulationRetryListener(callback: (assetSetID: string) => void) { this.populationRetryListeners.add(callback); }
    removePopulationRetryListener(callback: (assetSetID: string) => void) { this.populationRetryListeners.delete(callback); }
    retryPopulation(assetSetID: string) {
        this.setPopulationStatus([assetSetID], 'pending');
        [...this.populationRetryListeners].forEach(callback => callback(assetSetID));
    }

    reset() {
        this.populationStatusByAssetSetID.clear();
        if (this.configuration) this.commit(null);
    }
}

export default new AssetSetVisualizationStore();
